import { Box, Grid, Typography } from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import Topbar from "../global/Topbar";
import {
  createHistoryByExercise,
  updateHistoryById,
  deleteHistoryById,
  getExcerciseById,
  getRoutineByDay,
} from "../../redux/actions";
import { tokens } from "../../theme";
import ExerciseHeader from "./components/ExerciseHeader";
import HistorySection from "./components/HistorySection";
import SessionModal from "./components/SessionModal";
import RestTimerModal from "./components/RestTimerModal";

const ExerciseManagement = () => {
  const { exerciseId, day } = useParams();
  const dispatch = useDispatch();
  const exercise = useSelector((state) => state.Exercise);
  const routine = useSelector((state) => state.Routine);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [username, setUsername] = useState("");
  const [note, setNote] = useState("");
  const [sets, setSets] = useState([]);
  const [routineBlock, setRoutineBlock] = useState(null);
  const [allExercises, setAllExercises] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastSession, setLastSession] = useState(null);
  const [journeyMode, setJourneyMode] = useState(false);
  const [restModalOpen, setRestModalOpen] = useState(false);
  const [restDuration, setRestDuration] = useState(60);
  const [restRemaining, setRestRemaining] = useState(60);
  const [restRunning, setRestRunning] = useState(false);
  const restTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const currentUser = localStorage.getItem("currentUser") || "Vannesa Santamaria";
  const [userFilter, setUserFilter] = useState(currentUser); // currentUser or "all"

  // Inicializar AudioContext para iOS
  const initAudioContext = () => {
    if (!audioContextRef.current) {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        if (audioContext.state === 'suspended') {
          audioContext.resume();
        }
        audioContextRef.current = audioContext;
      } catch (e) {
        console.log('No se pudo inicializar AudioContext:', e);
      }
    }
  };

  const quickNotes = [
    "Subir peso",
    "Bajar reps",
    "Fallo técnico",
    "Buena técnica",
    "Recortar descanso",
    "Con máquina",
    "Con mancuernas",
    "Con cinturón",
    "Con spotter",
    "Con pausa",
    "Con straps",
    "Sin straps",
    "Sin cinturón",
    "Sin spotter",
    "Sin pausa",
  ];

  useEffect(() => {
    const currentUser = localStorage.getItem("currentUser");
    if (currentUser) {
      setUsername(currentUser);
    }
  }, []);

  // Obtener última sesión del usuario actual
  useEffect(() => {
    if (exercise.history && exercise.history.length > 0 && username) {
      const userHistory = exercise.history.filter((h) => h.username === username);
      if (userHistory.length > 0) {
        setLastSession(userHistory[userHistory.length - 1]);
        return;
      }
    }
    // Reset when no matching history to avoid leaking previous exercise data
    setLastSession(null);
  }, [exercise.history, username]);

  useEffect(() => {
    dispatch(getRoutineByDay(day));
    dispatch(getExcerciseById(exerciseId));
    setIsLoading(true);
  }, [dispatch, exerciseId, day]);

  useEffect(() => {
    if (routine?.description?.[day]) {
      const blocks = routine.description[day];
      const flatList = blocks.flatMap((block) => {
        if (block.type === "single") {
          return [
            {
              ...block,
              exerciseId: block.exerciseId,
              type: "single",
              position: block.position,
            },
          ];
        } else if (block.type === "superset") {
          return block.exercises.map((ex) => ({
            ...ex,
            type: "superset",
            position: block.position,
          }));
        }
        return [];
      });

      setAllExercises(flatList);
      const block = flatList.find((b) => b.exerciseId === exerciseId);
      setRoutineBlock(block);
      if (block) {
        const initialSets = Array(block.sets)
          .fill()
          .map(() => ({ reps: block.reps, weight: "" }));
        setSets(initialSets);
      }

      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  }, [routine, day, exerciseId]);

  const handleChangeSet = (index, field, value) => {
    const withTotal = (set) => {
      const weightLb = Number(set.weightLb || 0);
      const weightKg = Number(set.weightKg || 0);
      return {
        ...set,
        weightLb,
        weightKg,
        total: weightLb * 0.453592 + weightKg,
      };
    };

    const newSets = [...sets];
    newSets[index] = withTotal({ ...newSets[index], [field]: Number(value) });
    setSets(newSets);
  };

  const handleAddSubSet = (index) => {
    const newSets = [...sets];
    newSets.splice(index + 1, 0, { reps: "", weight: "" });
    setSets(newSets);
  };

  const handleDeleteSet = (index) => {
    const newSets = [...sets];
    newSets.splice(index, 1);
    setSets(newSets);
  };

  const handleCopyLastSession = () => {
    if (lastSession && lastSession.setData) {
      const withTotal = (set) => {
        const weightLb = Number(set.weightLb || 0);
        const weightKg = Number(set.weightKg || 0);
        return {
          ...set,
          weightLb,
          weightKg,
          total: weightLb * 0.453592 + weightKg,
        };
      };
      setSets(lastSession.setData.map((s) => withTotal(s)));
    }
  };

  const handleCopyToAll = (index, field) => {
    const base = sets[index];
    const withTotal = (set) => {
      const weightLb = Number(set.weightLb || 0);
      const weightKg = Number(set.weightKg || 0);
      return {
        ...set,
        weightLb,
        weightKg,
        total: weightLb * 0.453592 + weightKg,
      };
    };

    const updated = sets.map((set, i) => {
      if (i >= index) {
        return withTotal({ ...set, [field]: base[field] });
      }
      return set;
    });

    setSets(updated);
  };

  const handleCopySetDown = (index) => {
    const source = sets[index];
    if (!source) return;
    const withTotal = (set) => {
      const weightLb = Number(set.weightLb || 0);
      const weightKg = Number(set.weightKg || 0);
      return {
        ...set,
        weightLb,
        weightKg,
        total: weightLb * 0.453592 + weightKg,
      };
    };

    const updated = sets.map((set, i) => {
      if (i >= index) {
        return withTotal({
          ...set,
          reps: source.reps,
          weightLb: source.weightLb,
          weightKg: source.weightKg,
        });
      }
      return set;
    });

    setSets(updated);
  };

  const handleSkip = () => {
    const currentIndex = allExercises.findIndex(
      (ex) => ex.exerciseId === routineBlock?.exerciseId
    );
    const hasNext = currentIndex !== -1 && currentIndex < allExercises.length - 1;

    if (hasNext) {
      handleNavigate("next", true);
    } else {
      setModalOpen(false);
      setJourneyMode(false);
      setViewModalOpen(false);
      setSelectedRecord(null);
    }
  };

  const handleQuickNote = (text) => {
    if (!text) return;
    // Evita duplicar la misma nota consecutiva
    const normalized = text.trim();
    const current = note.trim();
    const separator = current ? " | " : "";
    if (current.includes(normalized)) return;
    setNote(`${current}${separator}${normalized}`);
  };

  const handleSubmit = (advanceToNext = false) => {
    const formattedSets = sets.map((s, index) => ({
      position: index + 1,
      reps: Number(s.reps),
      weightLb: Number(s.weightLb || 0),
      weightKg: Number(s.weightKg || 0),
      total: Number((s.weightLb || 0) * 0.453592 + (s.weightKg || 0)),
    }));

    const payload = {
      exerciseId: exercise.id,
      date,
      username,
      setData: formattedSets,
      isSuperset: routineBlock?.type === "superset",
      notes: note,
    };

    if (selectedRecord && selectedRecord.id) {
      const confirmEdit = window.confirm("¿Estás seguro de que deseas modificar este registro?");
      if (!confirmEdit) return;
      dispatch(updateHistoryById(selectedRecord.id, payload, exerciseId));
    } else {
      dispatch(createHistoryByExercise(payload));
    }

    const currentIndex = allExercises.findIndex(
      (ex) => ex.exerciseId === routineBlock?.exerciseId
    );
    const hasNext = currentIndex !== -1 && currentIndex < allExercises.length - 1;

    if (advanceToNext && hasNext) {
      setSelectedRecord(null);
      setViewModalOpen(false);
      handleNavigate("next", true);
      return;
    }

    setModalOpen(false);
    setViewModalOpen(false);
    setSelectedRecord(null);
  };

  const handleDelete = () => {
    if (selectedRecord && selectedRecord.id) {
      const confirmDelete = window.confirm("¿Estás seguro de que deseas eliminar este registro?");
      if (!confirmDelete) return;
      dispatch(deleteHistoryById(selectedRecord.id, exerciseId));
      setViewModalOpen(false);
      setSelectedRecord(null);
    }
  };

  const handleNavigate = (direction, keepModalOpen = false) => {
    if (!routineBlock) return;
    const currentIndex = allExercises.findIndex(
      (ex) => ex.exerciseId === routineBlock.exerciseId
    );
    const nextIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;
    const nextExercise = allExercises[nextIndex];
    if (nextExercise) {
      navigate(`/routines/${day}/${nextExercise.exerciseId}`);
      if (!keepModalOpen) {
        setModalOpen(false);
        setViewModalOpen(false);
        setSelectedRecord(null);
        setJourneyMode(false);
      } else {
        setSelectedRecord(null);
        setViewModalOpen(false);
        setModalOpen(true);
        setJourneyMode(true);
      }
    }
  };

  const handleRowDoubleClick = (params) => {
    const record = params.row;
    setSelectedRecord(record);
    setDate(record.date?.slice(0, 10));
    setUsername(record.username);
    setNote(record.notes || "");
    setSets(record.setData || []);
    setViewModalOpen(true);
  };

  useEffect(() => {
    return () => {
      if (restTimerRef.current) {
        clearInterval(restTimerRef.current);
      }
    };
  }, []);

  const notifyRestEnd = () => {
    console.log('notifyRestEnd called!');
    
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate([300, 200, 300, 200, 300]);
    }
    
    // Reproducir archivo de audio real de campana de box
    try {
      const bellAudio = new Audio('/sounds/boxing_bell.mp3');
      bellAudio.volume = 1.0; // Volumen máximo
      
      // Asegurar que está cargado antes de reproducir
      bellAudio.oncanplaythrough = () => {
        bellAudio.play().then(() => {
          console.log('Campana de box reproducida exitosamente');
        }).catch(e => {
          console.error('Error reproduciendo audio:', e);
        });
      };
      
      // Intentar reproducir inmediatamente también
      bellAudio.play().then(() => {
        console.log('Campana de box reproducida exitosamente');
      }).catch(e => {
        console.error('Error reproduciendo audio (intento 1):', e);
      });
    } catch (e) {
      console.error('Error creando elemento de audio:', e);
    }
  };

  const playTickSound = () => {
    try {
      const tickAudio = new Audio('/sounds/tick.mp3');
      tickAudio.volume = 0.5; // Volumen moderado para no ser molesto
      tickAudio.play().catch(e => {
        console.error('Error reproduciendo tick:', e);
      });
    } catch (e) {
      console.log('Error al reproducir tick:', e);
    }
  };

  const startRest = (durationSeconds) => {
    const seconds = Number(durationSeconds) || 0;
    if (seconds <= 0) return;
    
    // Inicializar AudioContext para iOS y pre-cargar audio
    initAudioContext();
    
    // Pre-cargar el audio para asegurar que está listo
    try {
      const bellAudio = new Audio('/sounds/boxing_bell.mp3');
      bellAudio.volume = 1.0;
      // No reproducir, solo cargar
      bellAudio.load();
    } catch (e) {
      console.log('Error pre-cargando audio:', e);
    }
    
    if (restTimerRef.current) clearInterval(restTimerRef.current);
    setRestDuration(seconds);
    setRestRemaining(seconds);
    setRestRunning(true);

    restTimerRef.current = setInterval(() => {
      setRestRemaining((prev) => {
        const newValue = prev - 1;
        
        if (newValue <= 0) {
          clearInterval(restTimerRef.current);
          restTimerRef.current = null;
          setRestRunning(false);
          notifyRestEnd();
          return 0;
        }
        return newValue;
      });
    }, 1000);
  };

  const stopRest = () => {
    if (restTimerRef.current) clearInterval(restTimerRef.current);
    restTimerRef.current = null;
    setRestRunning(false);
    setRestRemaining(restDuration);
  };

  const formatSeconds = (secs) => {
    const m = Math.floor(secs / 60)
      .toString()
      .padStart(2, "0");
    const s = Math.floor(secs % 60)
      .toString()
      .padStart(2, "0");
    return `${m}:${s}`;
  };

  const totalExercises = allExercises.length;
  const currentExerciseIndex = allExercises.findIndex(
    (ex) => ex.exerciseId === routineBlock?.exerciseId
  );
  const hasPrevExercise = currentExerciseIndex > 0;
  const hasNextExercise = currentExerciseIndex !== -1 && currentExerciseIndex < totalExercises - 1;

  // Formatea la fecha sin desfasar por zona horaria
  const formatDate = (value) => {
    if (!value) return "";
    // Si es un ISO completo con zona UTC, forzamos timeZone UTC al formatear
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString("es-ES", {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC",
      });
    }
    // Si viene como YYYY-MM-DD, construimos con Date.UTC
    const dateOnly = String(value).slice(0, 10);
    const [y, m, d] = dateOnly.split("-").map(Number);
    if (y && m && d) {
      return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("es-ES", {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC",
      });
    }
    return String(value);
  };

  const columns = [
    {
      field: "date",
      headerName: "Fecha",
      flex: 1,
      renderCell: (params) => formatDate(params.value),
    },
    { field: "username", headerName: "Usuario", flex: 1 },
    {
      field: "setData",
      headerName: "Series",
      flex: 2,
      renderCell: (params) => (
        <Box>
          {params.value.map((s, i) => (
            <Typography key={i} variant="body2">
              #{i + 1}: {s.reps} reps — {s.weightLb} lb + {s.weightKg} kg = {s.total?.toFixed(2)} kg
            </Typography>
          ))}
        </Box>
      ),
    },
    { field: "notes", headerName: "Notas", flex: 2 },
  ];

  const openNewRecordModal = () => {
    setSelectedRecord(null);
    setNote("");
    const currentUser = localStorage.getItem("currentUser");
    setUsername(currentUser || "Vannesa Santamaria");
    setDate(new Date().toISOString().slice(0, 10));

    if (routineBlock) {
      const defaultSets = Array(routineBlock.sets)
        .fill()
        .map(() => ({ reps: routineBlock.reps, weightLb: 0, weightKg: 0, total: 0 }));
      setSets(defaultSets);
    } else {
      setSets([]);
    }

    setModalOpen(true);
  };

  const closeModals = () => {
    setModalOpen(false);
    setViewModalOpen(false);
    setSelectedRecord(null);
  };

  const handleRestDurationChange = (e) => {
    if (restRunning) return;
    const val = e.target.value === "" ? "" : Number(e.target.value);
    setRestDuration(val);
    if (val !== "") {
      setRestRemaining(val);
    }
  };

  const handlePresetSelect = (sec) => {
    if (!restRunning) {
      setRestDuration(sec);
      setRestRemaining(sec);
    }
  };

  const handleStartStopRest = () => {
    if (restRunning) {
      stopRest();
    } else {
      startRest(restDuration);
    }
  };

  return (
    <Box>
      <Topbar />
      <Box sx={{ padding: { md: "0 40px", xs: "0px 20px" }, mt: "30px" }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: 3,
                backgroundColor: colors.grey[900],
              }}
            >
              <ExerciseHeader
                exercise={exercise}
                routineBlock={routineBlock}
                routine={routine}
                day={day}
                isLoading={isLoading}
                colors={colors}
                onRegisterClick={openNewRecordModal}
                onRestClick={() => setRestModalOpen(true)}
                onNavigatePrev={() => handleNavigate("prev")}
                onNavigateNext={() => handleNavigate("next")}
                hasPrevExercise={hasPrevExercise}
                hasNextExercise={hasNextExercise}
                userFilter={userFilter}
                onUserFilterChange={setUserFilter}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <HistorySection
              isLoading={isLoading}
              rows={(exercise.history || []).filter(h => userFilter === "all" || h.username === userFilter)}
              columns={columns}
              onRowDoubleClick={handleRowDoubleClick}
            />
          </Grid>
        </Grid>

        <SessionModal
          open={modalOpen || viewModalOpen}
          onClose={closeModals}
          selectedRecord={selectedRecord}
          lastSession={lastSession}
          colors={colors}
          username={username}
          setUsername={setUsername}
          date={date}
          setDate={setDate}
          sets={sets}
          handleChangeSet={handleChangeSet}
          handleAddSubSet={handleAddSubSet}
          handleDeleteSet={handleDeleteSet}
          handleCopyToAll={handleCopyToAll}
          handleCopySetDown={handleCopySetDown}
          note={note}
          setNote={setNote}
          quickNotes={quickNotes}
          handleQuickNote={handleQuickNote}
          handleSubmit={handleSubmit}
          handleDelete={handleDelete}
          routineBlock={routineBlock}
          onCopyLastSession={handleCopyLastSession}
        />

        <RestTimerModal
          open={restModalOpen}
          onClose={() => {
            setRestModalOpen(false);
            stopRest();
          }}
          restDuration={restDuration}
          restRemaining={restRemaining}
          restRunning={restRunning}
          onDurationChange={handleRestDurationChange}
          onStartStop={handleStartStopRest}
          onPresetSelect={handlePresetSelect}
          formatSeconds={formatSeconds}
        />
      </Box>
    </Box>
  );
};

export default ExerciseManagement;
